import { z } from 'zod';
import { EXPORT_ENCODINGS } from './enums.js';
import { valueListItemStatusSchema } from './enums.js';
import { booleanQueryParam } from './query-params.js';
/**
 * Request and response shapes for value list administration.
 *
 * Separate from `value-list.ts`, which describes the entities themselves: these
 * are the wire contracts, and they change for different reasons.
 */
export const valueListSummarySchema = z.object({
    id: z.uuid(),
    key: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    system: z.boolean(),
    allowClientCreation: z.boolean(),
    /** Live entries, so the portal can show a count without a second request. */
    itemCount: z.number().int().min(0),
    /** Entries a capturer created that are still waiting for review. */
    pendingCount: z.number().int().min(0),
});
export const valueListUpdateSchema = z
    .object({
    name: z.string().min(1).max(200).optional(),
    description: z.string().max(1000).nullable().optional(),
    /** Whether the app may propose new entries while offline. */
    allowClientCreation: z.boolean().optional(),
})
    .refine((value) => Object.keys(value).length > 0, {
    message: 'Es wurde keine Änderung angegeben',
});
export const valueListCreateSchema = z.object({
    key: z
        .string()
        .min(1)
        .max(64)
        .regex(/^[a-z][a-z0-9_]*$/, 'Der Key muss klein geschrieben sein und mit einem Buchstaben beginnen'),
    name: z.string().min(1).max(200),
    description: z.string().max(1000).nullable().default(null),
    allowClientCreation: z.boolean().default(false),
});
export const valueListItemViewSchema = z.object({
    id: z.uuid(),
    valueListId: z.uuid(),
    label: z.string(),
    value: z.string(),
    metadata: z.record(z.string(), z.unknown()),
    status: valueListItemStatusSchema,
    mergedIntoId: z.uuid().nullable(),
    /** Label of the surviving entry, so a tombstone is readable without a lookup. */
    mergedIntoLabel: z.string().nullable(),
    sortOrder: z.number().int(),
    createdByUserId: z.uuid().nullable(),
    createdByName: z.string().nullable(),
    /** How many items currently reference this entry. */
    usageCount: z.number().int().min(0),
    createdAt: z.iso.datetime({ offset: true }),
    updatedAt: z.iso.datetime({ offset: true }),
});
export const VALUE_LIST_ITEM_SORT_FIELDS = ['label', 'value', 'sortOrder', 'createdAt'];
export const valueListItemSortSchema = z.enum(VALUE_LIST_ITEM_SORT_FIELDS);
export const valueListItemQuerySchema = z.object({
    /** Substring match against label, value and — for suppliers — the metadata. */
    search: z.string().max(200).optional(),
    status: valueListItemStatusSchema.optional(),
    /** Excludes merged and archived entries. On by default. */
    activeOnly: booleanQueryParam.default(true),
    sort: valueListItemSortSchema.default('label'),
    direction: z.enum(['asc', 'desc']).default('asc'),
    limit: z.coerce.number().int().min(1).max(500).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
export const valueListItemListResponseSchema = z.object({
    items: z.array(valueListItemViewSchema),
    total: z.number().int().min(0),
});
export const valueListItemInputSchema = z.object({
    label: z.string().min(1, 'Bitte eine Bezeichnung angeben').max(300),
    /** Defaults to the label when the list has no separate coding. */
    value: z.string().min(1).max(300).optional(),
    metadata: z.record(z.string(), z.unknown()).default({}),
    sortOrder: z.number().int().default(0),
});
export const valueListItemPatchSchema = z
    .object({
    label: z.string().min(1).max(300).optional(),
    value: z.string().min(1).max(300).optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
    sortOrder: z.number().int().optional(),
    /** Only active and archived; approval and merging have their own routes. */
    status: z.enum(['active', 'archived']).optional(),
})
    .refine((value) => Object.keys(value).length > 0, {
    message: 'Es wurde keine Änderung angegeben',
});
export const mergeRequestSchema = z.object({
    /** The entry that disappears. Kept as a tombstone. */
    sourceId: z.uuid(),
    /** The entry that survives. */
    targetId: z.uuid(),
});
export const mergeResponseSchema = z.object({
    sourceId: z.uuid(),
    targetId: z.uuid(),
    repointed: z.object({
        itemSupplier: z.number().int().min(0),
        itemAttributes: z.number().int().min(0),
        users: z.number().int().min(0),
    }),
});
export const approveRequestSchema = z.object({
    /** Optional corrections applied together with the approval. */
    label: z.string().min(1).max(300).optional(),
    value: z.string().min(1).max(300).optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
});
export const rejectRequestSchema = z.object({
    reason: z.string().max(500).optional(),
});
// --- CSV import --------------------------------------------------------------
/**
 * Which CSV column feeds which field.
 *
 * Indexes rather than header names: a file may have no header row at all, and
 * matching on names breaks the moment someone renames a column in Excel.
 */
export const importMappingSchema = z.object({
    label: z.number().int().min(0),
    value: z.number().int().min(0).nullable().default(null),
    sortOrder: z.number().int().min(0).nullable().default(null),
    /** Metadata target key per column index, e.g. `{ "1": "supplierNumber" }`. */
    metadata: z.record(z.string(), z.string()).default({}),
});
export const importOptionsSchema = z.object({
    /** Omitted means: detect from the file. */
    delimiter: z.string().length(1).optional(),
    encoding: z.enum(EXPORT_ENCODINGS).optional(),
    hasHeaderRow: z.boolean().default(true),
    mapping: importMappingSchema,
    /**
     * What to do when an entry with the same value already exists.
     * `skip` leaves it alone, `update` overwrites label, sort order and metadata.
     */
    onDuplicate: z.enum(['skip', 'update']).default('skip'),
});
export const importIssueSchema = z.object({
    /** 1-based line in the source file. */
    line: z.number().int().min(1),
    message: z.string(),
    /** The raw values of the offending row, for display. */
    values: z.array(z.string()),
});
export const importPreviewSchema = z.object({
    detectedEncoding: z.enum(EXPORT_ENCODINGS),
    detectedDelimiter: z.string(),
    /** Header row if one was present, otherwise generated column labels. */
    headers: z.array(z.string()),
    /** First rows, for the mapping UI. */
    sampleRows: z.array(z.object({ line: z.number().int(), values: z.array(z.string()) })),
    totalRows: z.number().int().min(0),
});
export const importResultSchema = z.object({
    created: z.number().int().min(0),
    updated: z.number().int().min(0),
    skipped: z.number().int().min(0),
    /** Rows that could not be imported, each with its line number and reason. */
    issues: z.array(importIssueSchema),
    detectedEncoding: z.enum(EXPORT_ENCODINGS),
    detectedDelimiter: z.string(),
});
//# sourceMappingURL=value-list-api.js.map